import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DatePicker } from '@/components/pages/room-manager/_room-manager/date-picker'
import { css } from '@emotion/core'
import { Button, ButtonType } from '@/components/atoms/button'
import { GuestRoomAssignStatusResponseType, StatusType } from '@/models/room-manager/response/guest-room-assign-status'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import moment from 'moment'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'
import { shouldRoomsFollowUnattachedRooms } from '@/utils/room-assign-scroll-sync'

type RoomSummaryBarProps = {
  reservation: AdminReservationResponseType['reservations'] | undefined
  guestRooms: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList'] | undefined
  showAssignPanel: boolean
  assignPanelEditMode: boolean
  roomManagerTopSelectDate: moment.Moment
  order: 'room' | 'floor'
  hiddenRoomNameList: string[]
  setRoomManagerTopSelectDate: React.Dispatch<React.SetStateAction<moment.Moment>>
  assignPanel: () => React.ReactChild
  onClickUnattachedStatus: (status: StatusType) => void
  editGuestRooms: EditRoomType[]
  setAutomaticAssignmentReservationsIds: (reservationsIds: string[]) => void
}

export const RoomSummaryBar: React.FC<RoomSummaryBarProps> = ({
  reservation,
  guestRooms,
  showAssignPanel,
  assignPanelEditMode,
  roomManagerTopSelectDate,
  order,
  hiddenRoomNameList,
  setRoomManagerTopSelectDate,
  assignPanel,
  onClickUnattachedStatus,
  editGuestRooms,
  setAutomaticAssignmentReservationsIds,
}) => {
  const { t } = useTranslation()
  const [unattachedGuestRoomsByType, setUnattachedGuestRoomsByType] = useState<any[]>([])
  const canBeAutoAssignment =
    unattachedGuestRoomsByType?.length && Object.values(unattachedGuestRoomsByType).some(roomType => roomType?.statuses?.length)

  const goToPrevDate = (day = 1) => {
    setRoomManagerTopSelectDate(moment(roomManagerTopSelectDate).subtract(day, 'days'))
  }
  const goToNextDate = (day = 1) => {
    setRoomManagerTopSelectDate(moment(roomManagerTopSelectDate).add(day, 'days'))
  }

  const getUnattachedStatuses = guestRoom => {
    const isFullByEdit = status => {
      if (editGuestRooms?.[0]?.reservationId === status.reservationId) {
        return editGuestRooms.every(r => r.roomNumber && r.roomNumber !== '指定なし')
      }
    }
    return (guestRoom['指定なし']?.statuses || []).filter(
      s =>
        s.guestRoomAssignId &&
        (moment(roomManagerTopSelectDate).isBetween(s.assignCheckinDate, s.assignCheckoutDate, 'date', '[)') ||
          moment(roomManagerTopSelectDate).isSame(s.assignCheckinDate, 'date')) &&
        !isFullByEdit(s),
    )
  }

  const onClickAutoAssignment = () => {
    setAutomaticAssignmentReservationsIds(
      unattachedGuestRoomsByType.reduce((sum, roomType) => [...sum, ...roomType.statuses.map(status => status.reservationId)], []),
    )
  }

  useEffect(() => {
    if (!guestRooms) return setUnattachedGuestRoomsByType([])

    setUnattachedGuestRoomsByType(
      Object.keys(guestRooms).map(roomType => ({
        type: roomType,
        statuses: getUnattachedStatuses(guestRooms[roomType]),
      })),
    )
  }, [guestRooms])

  useEffect(() => {
    const onScrollRooms = e => document.getElementById('unattachedRoomsContainer')?.scrollTo({ top: e.target?.scrollTop })
    const onScrollUnattachedRooms = (e: Event) => {
      const unattachedRooms = e.target as HTMLElement
      const rooms = document.getElementById('roomsContainer')
      if (rooms && shouldRoomsFollowUnattachedRooms(rooms.scrollTop, unattachedRooms)) rooms.scrollTo({ top: unattachedRooms.scrollTop })
    }

    document.getElementById('roomsContainer')?.addEventListener('scroll', onScrollRooms)
    document.getElementById('unattachedRoomsContainer')?.addEventListener('scroll', onScrollUnattachedRooms)

    return () => {
      document.getElementById('roomsContainer')?.removeEventListener('scroll', onScrollRooms)
      document.getElementById('unattachedRoomsContainer')?.removeEventListener('scroll', onScrollUnattachedRooms)
    }
  }, [])

  return (
    <div css={{ position: 'absolute', right: 0, top: 0, bottom: 0, paddingRight: 24, paddingTop: 24, zIndex: 13 }}>
      <div css={{ display: 'flex', height: '100%', boxShadow: '0px 0px 6px #00000029', backgroundColor: '#fff' }}>
        <div css={{ height: '100%', width: 194, display: 'flex', flexDirection: 'column' }}>
          <div css={datePickerContainer}>
            <div css={{ display: 'flex', gap: 6 }}>
              <Button buttonType={ButtonType.NonStyle} onClick={() => goToPrevDate(7)} width={48} height={24} fontSize={12}>
                {t('Prev week')}
              </Button>
              <Button
                buttonType={ButtonType.NonStyle}
                onClick={() => setRoomManagerTopSelectDate(moment())}
                width={48}
                height={24}
                fontSize={12}
              >
                {t('Today')}
              </Button>
              <Button buttonType={ButtonType.NonStyle} onClick={() => goToNextDate(7)} width={48} height={24} fontSize={12}>
                {t('Next week')}
              </Button>
            </div>
            <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img
                src={require(`@/static/images/arrow_left_gray.svg`)}
                css={{ width: 36, cursor: 'pointer' }}
                onClick={() => goToPrevDate()}
              />
              <DatePicker
                date={roomManagerTopSelectDate}
                setDate={setRoomManagerTopSelectDate}
                type={'select'}
                setRoomManagerTopSelectDate={setRoomManagerTopSelectDate}
                readOnly={true}
              />
              <img
                src={require(`@/static/images/arrow_right_gray.svg`)}
                css={{ width: 36, cursor: 'pointer' }}
                onClick={() => goToNextDate()}
              />
            </div>
            <div style={{ visibility: canBeAutoAssignment ? 'visible' : 'hidden' }}>
              <Button
                width={120}
                height={24}
                buttonType={showAssignPanel ? 5 : 2}
                fontSize={12}
                onClick={onClickAutoAssignment}
                isDisabled={showAssignPanel}
              >
                {t('Automatic assignment')}
              </Button>
            </div>
          </div>
          <div css={{ paddingBottom: 8, overflowX: 'hidden', overflowY: 'auto', flex: 1 }} id="unattachedRoomsContainer">
            {guestRooms &&
              unattachedGuestRoomsByType.map(item => {
                const roomType = item.type
                const unattachedStatuses = item.statuses

                // if (assignPanelEditMode && unattachedEditStatuses[roomType]) {
                //     unattachedStatuses.push(...unattachedEditStatuses[roomType].map(s => ({ ...s, isEditingNow: true })))
                // }

                return (
                  <div key={roomType}>
                    {order !== 'floor' && (
                      <div
                        css={{
                          height: 48,
                          display: 'flex',
                          padding: '0 16px',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: '#FAFAFA',
                          marginTop: '-1px',
                          borderTop: '1px solid #E3E3E3',
                          borderBottom: '1px solid #E3E3E3',
                        }}
                      >
                        <div
                          css={{
                            fontSize: 12,
                            lineHeight: '15px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {roomType}
                        </div>
                        {!!unattachedStatuses.length && (
                          <div
                            css={{
                              minWidth: 18,
                              height: 18,
                              padding: '0 3px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 'bold',
                              backgroundColor: '#f2a40b',
                              borderRadius: 9,
                              color: '#fff',
                            }}
                          >
                            {unattachedStatuses.length}
                          </div>
                        )}
                      </div>
                    )}
                    {!hiddenRoomNameList.includes(roomType) && (
                      <div
                        css={{
                          height: order !== 'floor' ? Math.max(Object.keys(guestRooms[roomType] || {}).length - 1, 0) * 42 : '100%',
                          overflowY: 'auto',
                        }}
                      >
                        {unattachedStatuses.map((s: any) => {
                          const isSelected = reservation?.reservationId === s.reservationId || s.isEditingNow
                          return (
                            <div
                              key={s.guestRoomAssignId}
                              css={{
                                height: 42,
                                padding: '0 16px',
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: '1px solid #F2F2F2',
                                fontSize: 12,
                              }}
                            >
                              <div
                                css={{
                                  borderRadius: 14,
                                  padding: '0 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  height: 28,
                                  cursor: !isSelected ? 'pointer' : undefined,
                                  border: !isSelected ? '1px solid #ccc' : undefined,
                                  color: isSelected ? '#fff' : undefined,
                                  backgroundColor: isSelected ? '#676767' : undefined,
                                }}
                                onClick={() => onClickUnattachedStatus(s)}
                              >
                                <div css={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.userName || '-'}</div>
                                <div css={{ whiteSpace: 'nowrap' }}>
                                  {s.checkinDate && s.checkoutDate ? dayjs(s.checkoutDate).diff(s.checkinDate, 'day') : '-'}
                                  {t('Night')}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
        {showAssignPanel && <div css={{ width: 270, height: '100%', borderLeft: '1px solid #F2F2F2' }}>{assignPanel()}</div>}
      </div>
    </div>
  )
}

const datePickerContainer = css({
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  // Add RemainingRoomTotal height(29px)
  height: 'calc(90px + 29px)',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: '1px solid #E3E3E3',
  '.DateInput_input': {
    fontWeight: 'bold',
  },
  '.SingleDatePickerInput': {
    border: 'none',
    textAlign: 'center',
    width: 130,
    '.SingleDatePickerInput_calendarIcon': { display: 'none' },
  },
  '.SingleDatePicker_picker.SingleDatePicker_picker__directionLeft': {
    top: '32px !important',
    left: '-150px !important',
  },
})
